// src/components/Siniestros/SiniestroLogsCompGrid.js
import ColumnFilter from 'components/ui/Table/Filters/ColumnFilter'
import DateRangeColumnFilter from 'components/ui/Table/Filters/DateRangeColumnFilter'
import SelectColumnFilter from 'components/ui/Table/Filters/SelectColumnFilter'
import Table from 'components/ui/Table/Table'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'

/* eslint-disable react/prop-types */

const formatDateTimeOnly = (value) => {
  if (!value) return ''
  const str = String(value)
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (m) return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`
  const d = new Date(value)
  if (isNaN(d.getTime())) return str
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Filtro multi-select: filterValue es un array de strings
const makeMultiSelectFilter = () => {
  const fn = (rows, id, filterValue) => {
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return rows
    const values = Array.isArray(filterValue) ? filterValue : [filterValue]
    return rows.filter((row) => values.includes(String(row.values[id] || '').trim()))
  }
  fn.autoRemove = (val) => !val || (Array.isArray(val) && val.length === 0)
  return fn
}

const filterByEstado     = makeMultiSelectFilter()
const filterByUsuario    = makeMultiSelectFilter()
const filterByDelegacion = makeMultiSelectFilter()

export default function SiniestroLogsCompGrid({ data, handleRefresh }) {
  const [tableData, setTableData] = useState(null)

  const customDateTimeSort = (rowA, rowB, columnId) => {
    const a = rowA?.values?.[columnId]
    const b = rowB?.values?.[columnId]
    const da = a ? new Date(a) : null
    const db = b ? new Date(b) : null
    if (!da || isNaN(da.getTime())) return 1
    if (!db || isNaN(db.getTime())) return -1
    return da.getTime() - db.getTime()
  }

  const columns = useMemo(() => [
    {
      Header: 'Fecha y hora',
      accessor: 'Log_FechaHora',
      minWidth: 160,
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween',
      sortType: customDateTimeSort,
      Cell: ({ value }) => formatDateTimeOnly(value),
      exportFormat: (value) => formatDateTimeOnly(value),
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'ID Interno',
      accessor: 'Log_IDInterno',
      minWidth: 120,
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true,
      Cell: ({ value }) => <span>{value || '—'}</span>
    },
    {
      Header: 'Estado',
      accessor: 'Log_Estado',
      minWidth: 200,
      Filter: SelectColumnFilter,
      filter: filterByEstado,
      showInPDF: true,
      showInExcel: true,
      Cell: ({ value }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {value || '—'}
        </span>
      )
    },
    {
      Header: 'Delegación',
      accessor: 'Log_Delegacion',
      minWidth: 140,
      Filter: ColumnFilter,
      filter: filterByDelegacion,
      showInPDF: true,
      showInExcel: true,
      Cell: ({ value }) => <span>{value || '—'}</span>
    },
    {
      Header: 'Usuario',
      accessor: 'Log_Usuario',
      minWidth: 180,
      Filter: SelectColumnFilter,
      filter: filterByUsuario,
      showInPDF: true,
      showInExcel: true,
      Cell: ({ value }) => <span>{value || '—'}</span>
    }
  ], [])

  useEffect(() => {
    if (data === undefined || data === null) { setTableData(null); return }
    if (Array.isArray(data)) {
      setTableData(
        data.map((item) => ({
          Log_Id:          item.id,
          Log_IDInterno:   item.IDInterno || '',
          Log_Estado:      item.estado || '',
          Log_Delegacion:  item.delegacion_propietaria || '',
          Log_FechaHora:   item.changed_at,
          Log_Usuario:     String(item.changed_by_full_name || '').trim()
        }))
      )
    } else {
      setTableData([])
    }
  }, [data])

  if (!tableData) {
    return (
      <div className="flex items-center content-center justify-center w-full h-full">
        <p className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700" title="Cargando...">
          <ImSpinner9 size={70} className="animate-spin" />
        </p>
      </div>
    )
  }

  return (
    <Table
      tableName="Historial de estados"
      columns={columns}
      createNewItemURL="/siniestros/grid"
      data={tableData}
      defaultSort={{ id: 'Log_FechaHora', desc: true }}
      hiddenColumns={[]}
      allowImportExcel={false}
      handleRefresh={handleRefresh}
    />
  )
}

SiniestroLogsCompGrid.propTypes = {
  data: PropTypes.array,
  handleRefresh: PropTypes.func.isRequired
}